import { useEffect, useState } from "react";
import { GetV2Channel_getV2Channel_distributions } from "../__generated__/GetV2Channel";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, TextField } from "@mui/material";
import { useMutation } from "react-apollo";
import { toast } from "react-toastify";
import { TOAST_SETTINGS } from "../../../../BillingDetails/AddPaymentMethodDialog";
import { GET_V2_CHANNEL } from "../V2ChannelDetails";
import HESPFormLabel from "../../../../HESPFormLabel/HESPFormLabel";
import gql from "graphql-tag";
import { CloneV2Distribution, CloneV2DistributionVariables } from "./__generated__/CloneV2Distribution";

const CLONE_V2_DISTRIBUTION = gql`
    mutation CloneV2Distribution($input: CloneV2DistributionInput!) {
        cloneV2Distribution(input: $input) {
            id
            name
        }
    }
`;

interface Props {
    distribution: Omit<GetV2Channel_getV2Channel_distributions, "__typename">;
    open: boolean;
    organizationId: string;
    channelId: string;
    onClose: () => void;
}

export default function CloneDistributionDialog({ onClose, open, distribution, channelId, organizationId }: Props) {
    const [name, setName] = useState<string>("");

    const [cloneDistribution, { loading }] = useMutation<CloneV2Distribution, CloneV2DistributionVariables>(
        CLONE_V2_DISTRIBUTION,
    );

    useEffect(() => {
        if (open && distribution) {
            setName(`${distribution.name} - Copy`);
        }
    }, [open, distribution]);

    async function onClone(): Promise<void> {
        try {
            await cloneDistribution({
                variables: {
                    input: {
                        organizationId,
                        distributionId: distribution.id,
                        name,
                    },
                },
                refetchQueries: [
                    {
                        query: GET_V2_CHANNEL,
                        variables: {
                            organizationId,
                            channelId,
                        },
                    },
                ],
            });
            onClose();
            toast.success("Distribution cloned successfully");
        } catch (e: any) {
            toast.error(e.graphQLErrors[0]?.message ?? "Something went wrong 😞", TOAST_SETTINGS);
        }
    }

    const isValidName = name.trim().length > 0;

    return (
        <Dialog open={open} fullWidth>
            <DialogTitle>Clone distribution</DialogTitle>
            <DialogContent>
                <div className="mb-4">
                    <div className="text-sm text-gray-600 mb-2">
                        Cloning: <strong>{distribution?.name}</strong>
                    </div>
                </div>

                <FormControl fullWidth className="mb-4">
                    <HESPFormLabel label="New distribution name" />
                    <TextField type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" />
                </FormControl>
            </DialogContent>
            <DialogActions className="flex flex-row gap-2 justify-end">
                <Button variant="contained" color="inherit" onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    disabled={loading || !isValidName}
                    variant="contained"
                    color="primary"
                    onClick={() => onClone()}
                >
                    Clone
                </Button>
            </DialogActions>
        </Dialog>
    );
}
